import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { RequestStatusService } from 'src/app/components/requeststatus/requeststatus.service';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { MarkFormGroupTouchedAndDirty } from 'src/app/directives/mark-form-group-as-touched-dirty.function';
import { StatusRequestModel } from 'src/app/models/status/status-request.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ModalOpenerService } from '../modal-opener.service';
import CloudItem from 'src/app/models/clouditem';
import { BuilderRequestAutoExportSettingsModel } from 'src/app/models/builder/builder-request-auto-export-settings.model';
import { StatusRequestApiClient } from 'src/app/services/api-clients/status-request.api-client';
import { RequestActionTypeEnum } from 'src/app/constants/request-action-type.enum';
import { VerifyRequestActionService } from 'src/app/services/verify-request-action.service';
import { MinimumWordCountValidator } from 'src/app/directives/minimum-word-count-validator.directive';

export const StatusEditRequestOptions: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  centered: true,
  size: 'md'
};

@Component({
  templateUrl: './status-edit-request.modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class StatusEditRequestModal implements OnInit {
  private _request!: StatusRequestModel;
  @Input() set request(val: StatusRequestModel) {
    this._request = val;
  }
  get request(): StatusRequestModel {
    return this._request;
  }

  public translations = <any>{};
  public name!: string;
  public form!: UntypedFormGroup;
  public requestNameValue: FormControl;
  public workgroups = this.authenticationService.MyWorkgroups;
  public currentWorkgroup: string = "";
  public currentWorkgroupName: any;
  public currentClearancelevel: any;
  public clearancelevel: number = 1;
  public maxWorkgroupClearanceLevel: any = 1;
  public newClearancelevel: number | undefined;
  public newWorkgroupId: any;
  public newWorkgroupName: string | undefined;

  get autoExportEnabled() {
    return this.request && this.GetAutoExportEnabled();
  }

  get hasCloudItemSet(): boolean {
    if (!this.request) {
      return false;
    }
    return this.request.autoExportModel != null && this.request.autoExportModel != undefined && this.request.autoExportModel.cloudItem != null;
  }
  /**
   * :FRONTEND PROPERTY: Whether the Organisation default location should be used
   */
  get HasSpecificCloudItem(): boolean {
    if (!this.request) {
      return false;
    }
    return this.request.autoExportModel != null && this.request.autoExportModel != undefined && (this.request.autoExportModel.cloudItem != undefined && this.request.autoExportModel.cloudItem != null);
  };
  get selectedfolderPath(): string {
    if (!this.request || this.GetAutoExportPath() == "") {
      return this.translateService.instant("Request.RequestTitle.DisabledSinceNoLocation");
    }
    return this.GetAutoExportPath();
  }

  constructor(private formBuilder: UntypedFormBuilder, private activeModal: NgbActiveModal, private statusRequestApiClient: StatusRequestApiClient,
    private requestStatusService: RequestStatusService, private authenticationService: AuthenticationService,
    private translateService: TranslateService, private modalOpenerService: ModalOpenerService, private verifyRequestActionService: VerifyRequestActionService) {
    this.requestNameValue = new FormControl("", [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(500),
      MinimumWordCountValidator(1)
    ]);
    this.form = this.formBuilder.group(
      {
        requestName: this.requestNameValue,
        autoExport: new FormControl("")
      }
    );

    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    })
  }

  getTranslations() {
    this.translateService.get([
      'requestoverview.DialogChangeWorkgroup.SuccessfullMovedToOtherWorkgroup',
      'requestoverview.DialogChangeWorkgroup.SuccessfullMovedMultiToOtherWorkgroup',
      'requestoverview.DialogChangeWorkgroup.SuccessfullRemovedFromWorkgroup',
      'requestoverview.DialogChangeWorkgroup.SuccessfullRemovedMultiFromWorkgroup',
      'requestoverview.DialogChangeWorkgroup.NoWorkgroup',
      'Toasts.AddToWorkgroupError',
      'Toasts.Error'
    ])
      .subscribe(translation => {
        this.translations.successfullMovedToOtherWorkgroup = translation['requestoverview.DialogChangeWorkgroup.SuccessfullMovedToOtherWorkgroup'];
        this.translations.successfullMovedMultiToOtherWorkgroup = translation['requestoverview.DialogChangeWorkgroup.SuccessfullMovedMultiToOtherWorkgroup'];
        this.translations.successfullRemovedFromWorkgroup = translation['requestoverview.DialogChangeWorkgroup.SuccessfullRemovedFromWorkgroup'];
        this.translations.successfullRemovedMultiFromWorkgroup = translation['requestoverview.DialogChangeWorkgroup.SuccessfullRemovedMultiFromWorkgroup'];
        this.translations.NoWorkgroup = translation['requestoverview.DialogChangeWorkgroup.NoWorkgroup'];
        this.translations.AddToWorkgroupError = translation['Toasts.AddToWorkgroupError'];
        this.translations.error = translation['Toasts.Error'];
      });
  }

  ngOnInit() {
    this.currentClearancelevel = this.request.clearancelevel.toString();
    this.currentWorkgroup = this.request.workgroupGuid != undefined ? this.request.workgroupGuid : "";
    this.currentWorkgroupName = this.request.workgroupName != undefined ? this.request.workgroupName : "";
    this.maxWorkgroupClearanceLevel = this.workgroups.find(s => s.guid === this.request.workgroupGuid || s.guid === this.request.workgroupGuid) != undefined ? this.workgroups.find(s => s.guid === this.request.workgroupGuid || s.guid === this.request.workgroupGuid)?.clearancelevel : 1;
    this.newWorkgroupId = this.currentWorkgroup;
    this.newWorkgroupName = this.currentWorkgroupName;
    this.newClearancelevel = this.currentClearancelevel;
    this.form.controls['requestName'].setValue(this.request.requestName);
  }

  public closeModal() {
    this.activeModal.close();
  }

  public changeWorkgroupAllowed(): boolean {
    // Whether you are allowed
    return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.ChangeWorkGroup)
  }

  save() {
    if (this.form.invalid) {
      MarkFormGroupTouchedAndDirty(this.form);
      return;
    }
    let newRequestName = this.form.controls['requestName'].value;
    this.request.requestName = newRequestName;
    this.request.workgroupGuid = this.newWorkgroupId;
    this.request.clearancelevel = this.newClearancelevel ? this.newClearancelevel : 1;
    this.requestStatusService.updateRequest(this.request);
    this.closeModal();
  }

  changeCurrentClearancelevel(event: Event) {
    let clearancelevel: any = (event.target as HTMLInputElement).value;
    this.newClearancelevel = clearancelevel;
  }

  changeCurrentWorkgroup(event: Event) {
    let workgroupId: any = (event.target as HTMLInputElement).value;
    this.newWorkgroupId = workgroupId;
    var matchedWorkGroup = this.workgroups.find(s => s.guid === this.newWorkgroupId);
    this.newWorkgroupName = matchedWorkGroup?.name ?? "";
    this.maxWorkgroupClearanceLevel = matchedWorkGroup?.clearancelevel ?? 1;
    if (this.currentClearancelevel > this.maxWorkgroupClearanceLevel) {
      this.currentClearancelevel = 1;
    }
    if (!workgroupId) {
      this.maxWorkgroupClearanceLevel = 1;
      this.currentClearancelevel = 1;
    }
  }

  counter(min: number, max: number) {
    let numbers = [];
    for (let i = min; i <= max; i++) {
      numbers.push(i);
    }
    return numbers;
  }

  public openFolderSelection() {
    const modalRef = this.modalOpenerService.CloudFolderPicker(this.request.id, this.request, this.request.requestName);
    modalRef.componentInstance.close.subscribe(async (setCloudItem: CloudItem) => {
      modalRef.close();
    });
  }
  /**
   * :FRONTEND PROPERTY: Whether auto export is enabled
   */
  private GetAutoExportEnabled(): boolean {
    if (!this.request) {
      return false;
    }
    return this.request.autoExportModel != null && this.request.autoExportModel != undefined && this.request.autoExportModel.enabled;
  }

  private GetAutoExportPath(): string {
    if (this.HasSpecificCloudItem) {// this.request.autoExportModel && this.request.autoExportModel.cloudItem) {
      return this.request!.autoExportModel!.cloudItem!.providerName + "/" + this.request!.autoExportModel!.cloudItem!.folderPath;
    }
    return "";
  }

  /**
   *  :FRONTEND PROPERTY: Sets Specific Cloud Item or resets with NULL
   */
  private SetAutoExportSpecificCloudItem(cloudItem: CloudItem | null) {
    if (!this.request) {
      return;
    }
    if (!this.request.autoExportModel) {
      this.request.autoExportModel = new BuilderRequestAutoExportSettingsModel();
      this.request.autoExportModel.requestGuid = this.request.id;
    }
    this.request.autoExportModel.enabled = true;
    this.request.autoExportModel.cloudItem = cloudItem;
  }

  resetAutoExport() {
    this.SetAutoExportSpecificCloudItem(null);
    this.statusRequestApiClient.SetAutoExport(this.request.autoExportModel!);
  }
}
